import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeftOutlined, CloudUploadOutlined } from "@ant-design/icons";
import axios from "axios";

const SupplierEdit = () => {
  const [idBrand, setidBrand] = useState("");
  const [name, setName] = useState("");
  const [base64Image, setBase64Image] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const id = params.get("id");

  const baseUrl = `${process.env.REACT_APP_API_URL}/`;

  useEffect(() => {
    const fetchSupplierData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/brands/getidBrand/${id}`
        );
        const product = response.data;
        setidBrand(product.idBrand);
        setBase64Image(baseUrl + product.urlImage);
        setName(product.name);
      } catch (error) {
        console.log("Error fetching supplier data:", error);
        setErrorMessage("Không thể tải thông tin nhà cung cấp");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchSupplierData();
    }
  }, [id, baseUrl]);

  const [nameError, setNameError] = useState("");
  const [idBrandError, setIdBrandError] = useState("");
  const [urlImageError, setUrlImageError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      // Clear previous errors
      setNameError("");
      setIdBrandError("");
      setUrlImageError("");
      setErrorMessage("");

      let hasError = false;

      if (!name) {
        setNameError("Tên nhà cung cấp không được để trống.");
        hasError = true;
      }

      if (!idBrand) {
        setIdBrandError("Mã nhà cung cấp không được để trống.");
        hasError = true;
      }

      if (!base64Image) {
        setUrlImageError("Vui lòng chọn ảnh.");
        hasError = true;
      }

      if (hasError) {
        setIsSubmitting(false);
        return;
      }

      const data = {
        name: name,
        idBrand: idBrand,
        image: base64Image.replace(baseUrl, ""),
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/brands/updateidbrand/${id}`,
        data
      );

      if (response.status === 200) {
        setSuccessMessage("Cập nhật nhà cung cấp thành công");
        setShowSuccessModal(true);
      } else {
        setErrorMessage("Cập nhật nhà cung cấp thất bại");
      }
    } catch (error) {
      console.error("Error:", error);
      setErrorMessage("Có lỗi xảy ra khi cập nhật nhà cung cấp");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFile = (file) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Data = e.target.result;
        setBase64Image(base64Data);
        setUrlImageError(""); // Clear error when new image is selected
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage("");
  };

  const removeImage = () => {
    setBase64Image(null);
    setUrlImageError("");
  };

  // Show loading screen while fetching data
  if (isLoading) {
    return (
      <div className="bg-gray-50 h-screen overflow-y-auto">
        <div className="flex items-center justify-center h-full">
          <div className="flex items-center space-x-2 text-gray-500">
            <div className="w-8 h-8 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
            <span className="text-lg">Đang tải dữ liệu...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 h-screen overflow-y-auto">
      <div className="overflow-y-auto h-[calc(100vh-100px)] px-20 py-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center gap-4">
              <Link
                to="/admin/supplier"
                className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeftOutlined />
                Quay lại
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Chỉnh sửa nhà cung cấp
                </h1>
                <p className="text-gray-600 mt-1">
                  Cập nhật thông tin nhà cung cấp và thương hiệu
                </p>
              </div>
            </div>
          </div>

          {/* Main Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                Thông tin cơ bản
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mã nhà cung cấp <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={idBrand}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                    placeholder="Mã nhà cung cấp (không thể thay đổi)"
                  />
                  {idBrandError && (
                    <p className="mt-1 text-sm text-red-600">{idBrandError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tên nhà cung cấp <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      nameError
                        ? "border-red-300 focus:ring-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="Nhập tên nhà cung cấp"
                  />
                  {nameError && (
                    <p className="mt-1 text-sm text-red-600">{nameError}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Logo Upload */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                Logo thương hiệu
              </h2>

              <div className="space-y-4">
                <div
                  onDrop={handleFileDrop}
                  onDragOver={handleDragOver}
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    urlImageError
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"
                  }`}
                >
                  <div className="space-y-4">
                    <CloudUploadOutlined className="text-4xl text-gray-400" />
                    <div>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        <span className="text-blue-600 hover:text-blue-700 font-medium">
                          Chọn ảnh mới
                        </span>
                      </label>
                      <span className="text-gray-600">
                        {" "}
                        hoặc kéo thả ảnh vào đây
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">
                      Định dạng: JPG, PNG, GIF (tối đa 5MB)
                    </p>
                  </div>
                </div>

                {urlImageError && (
                  <p className="text-sm text-red-600">{urlImageError}</p>
                )}

                {/* Image Preview */}
                {base64Image && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {base64Image.startsWith("data:")
                          ? "Ảnh mới đã chọn:"
                          : "Ảnh hiện tại:"}
                      </span>
                      <button
                        type="button"
                        onClick={removeImage}
                        className="text-red-600 hover:text-red-700 text-sm"
                      >
                        Xóa
                      </button>
                    </div>
                    <div className="flex justify-center">
                      <img
                        src={base64Image}
                        alt="Logo preview"
                        className="w-32 h-32 object-cover rounded-lg border border-gray-200 shadow-sm"
                        onError={(e) => {
                          e.target.src =
                            "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIGZpbGw9IiNGM0Y0RjYiLz48cGF0aCBkPSJNMjEgMjFIMjNWMjNIMjFWMjFaTTQxIDIxSDQzVjIzSDQxVjIxWk0yNyAzOUgyOVY0MUgyN1YzOVpNMzEgMzlIMzNWNDFIMzFWMzlaIiBmaWxsPSIjOUIxMDFCIi8+PC9zdmc+";
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Error Message */}
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-600">{errorMessage}</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex justify-end space-x-4">
                <Link
                  to="/admin/supplier"
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Hủy
                </Link>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  {isSubmitting ? "Đang cập nhật..." : "Cập nhật nhà cung cấp"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Cập nhật thành công!
              </h3>
              <p className="text-gray-600 mb-6">
                Thông tin nhà cung cấp đã được cập nhật thành công.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Tiếp tục chỉnh sửa
                </button>
                <Link
                  to="/admin/supplier"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Về danh sách
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierEdit;
